import { Flex, Box, Button, Image, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { Fragment, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import scroll from "scroll"
import scrollDoc from "scroll-doc"
import styled from "styled-components"

import { toggleBuildYourOwn } from "areas/product/store/actions"
import {
  OptionTitle,
  OptionPrice,
} from "areas/product/wizard-options/components"
import { jewlr_t } from "helpers/application"

import CustomizeStone from "./customize-stone"
import RecommendedStones from "./recommended-stones"

const ChoiceOption = styled(Button).attrs({
  variant: "text",
})`
  background-color: white;
  border-radius: 5px;
  display: block;
  max-width: 190px;
  padding: 8px;
  text-align: center;
  transition: background-color 0.3s ease;
  width: calc(50% - 8px);
  ${(props) => props.theme.mediaQueries.tablet`
    width: 50%;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    width: calc(50% - 24px);
  `}
  ${(props) =>
    props.selected &&
    `
    background-color: ${props.theme.colors.primary};
    color: ${props.theme.colors.white};
    ${OptionTitle}, ${OptionPrice} {
      color: ${props.theme.colors.white};
    }
  `}
`
const scrollSelectorInView = (ref) => {
  if (ref.current) {
    const offset = ref.current.offsetTop - 100
    const page = scrollDoc()
    if (scrollY > offset) {
      scroll.top(page, offset)
    }
  }
}

const InternalChooseStone = ({
  choosePath,
  currentStepName,
  selectedShape,
}) => {
  const diamondSelectorRef = useRef(null)
  const firstElementRef = useRef()
  const [path, setPath] = useState(
    currentStepName === "choose_stone" ? "recommended" : "custom"
  )
  useEffect(() => {
    if (currentStepName === "choose_stone") {
      setPath("recommended")
    }
    scrollSelectorInView(diamondSelectorRef)
  }, [currentStepName])

  useEffect(() => {
    if (firstElementRef.current) {
      firstElementRef.current.focus()
    }
  }, [])

  const pathChoices = {
    custom: {
      description: (
        <>
          Choose your own cut,
          <br /> clarity and {jewlr_t("terms.color")}
        </>
      ),
      dimension: "60px",
      image: `${sl.config.jewlr_asset_url}images/product/diamond/${selectedShape}/0.50ct.svg`,
      title: "Select Your Own",
    },
    recommended: {
      description: (
        <>
          Diamonds we know <br />
          <span className="nowrap">you&apos;ll love</span>
        </>
      ),
      dimension: "40px",
      image:
        path === "recommended"
          ? `${require("images/layout/logo/jewlr-icon-logo-white.svg")}`
          : `${require("images/layout/logo/jewlr-icon-logo.svg")}`,
      style: { my: "10px" },
      title: "Recommended Stones",
    },
  }
  return (
    <>
      <Typography fontSize={14} mb={2}>
        Select your stone from <strong>Our Recommendations</strong> or choose{" "}
        <strong>Select Your Own</strong>.
      </Typography>
      <Flex justifyContent="center" mb={3}>
        {["recommended", "custom"].map((choice, i) => (
          <Fragment key={`diamond_path_${choice}`}>
            <ChoiceOption
              data-cy={`diamon-selector-stone-quality-${choice}`}
              onClick={() => {
                setPath(choice)
                choosePath(choice)
              }}
              selected={path === choice}
              {...(i === 0 && { ref: firstElementRef })}
            >
              <OptionPrice
                fontSize={{ _: "14px", tablet: "16px" }}
                height={{ _: "25px", tablet: "auto" }}
                mt={1}
              >
                {pathChoices[choice].title}
              </OptionPrice>

              <Image
                alt={selectedShape}
                height={pathChoices[choice].dimension}
                src={pathChoices[choice].image}
                width={pathChoices[choice].dimension}
                {...pathChoices[choice].style}
              />
              <OptionTitle fontWeight="normal">
                {pathChoices[choice].description}
              </OptionTitle>
            </ChoiceOption>
            {i === 0 && (
              <Box
                bg="grey.30"
                mx={{ _: "6px", desktop: "12px" }}
                width="1px"
              />
            )}
          </Fragment>
        ))}
      </Flex>
      <div ref={diamondSelectorRef}>
        {currentStepName === "choose_stone" ? (
          <RecommendedStones />
        ) : (
          <CustomizeStone />
        )}
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    currentStepName: state.diamondUI.steps[state.diamondUI.currentStep],
    selectedShape: state.choices.d_shape.value.toLowerCase(),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    choosePath: (path) => {
      dispatch(toggleBuildYourOwn(path === "custom"))
    },
  }
}

InternalChooseStone.propTypes = {
  choosePath: PropTypes.func,
  currentStepName: PropTypes.string,
  selectedShape: PropTypes.string,
}

const ChooseStone = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalChooseStone)

export default ChooseStone
